import React, { useCallback, useMemo, useState } from 'react';
import { ArrowRight, CaretLeft as ChevronLeft, CaretRight as ChevronRight } from '@phosphor-icons/react';
import { useCars } from '../context/CarsContext';
import { useDealerPreset } from '../config/dealerPresets';
import { useTranslation } from '../i18n/I18nContext';
import SalonCarCard from '../components/landing/SalonCarCard';
import SalonHeader from '../components/landing/SalonHeader';
import AutoScrollCarousel from '../components/ui/AutoScrollCarousel';
import { getDisplayImages } from '../utils/carImage';
import { useDealerScopeNavigate } from '../hooks/useDealerScopeNavigate';
import './SalonLandingPage.css';

/**
 * Salon Landing — «первый экран» салона после скана общего QR (issue #1022).
 *
 * Composition:
 *   1. Hero-photo background — фото первой машины + тёмный gradient overlay,
 *      чтобы UI читался поверх любой фотографии. Полностью на shell,
 *      content рендерится поверх z-index'ом.
 *   2. Header — full-width дилерская плашка: Autodis logo по центру (крупно)
 *      + sub-row OMODA/JAECOO (мельче). Без своего background-цвета —
 *      плашка использует тёмный gradient hero overlay.
 *   3. Main — auto-scroll carousel со slim `SalonCarCard` (фото + 3 спеки +
 *      цена, без year/mileage). Bouncing chevron на L+R edge сигнализирует
 *      «можно свайпать» (как на CarLandingPage); скрываются после первого
 *      ручного swipe + persist в localStorage.
 *   4. Footer — CTA «View all cars» прижат к нижнему краю экрана (sticky-style,
 *      по аналогии с «Learn more with AI» на CarLandingPage).
 *
 * URL: `/salon` (App.jsx + AppWidgetFlexible.jsx, gated by ENABLE_SHOWROOM_CATALOG).
 * Brand list + dealer preset захардкожены под Autodis-Luxembourg.
 */
const SWIPE_HINT_STORAGE_KEY = 'b2b-demo:salon-swipe-hint-seen';

const SalonLandingPage = () => {
  // Переносит dealer-scope параметры (?token) вперёд при навигации, чтобы
  // reload лендинга/каталога не потерял дилера → «Car not found».
  const dealerNavigate = useDealerScopeNavigate();
  const { getAllCars, loading, error } = useCars();
  const { t } = useTranslation();
  useDealerPreset('autodis');

  const rawCars = getAllCars();

  // Машины перемешиваем по бренду: OMODA → JAECOO → OMODA → JAECOO → ...
  // (Маша Welcome v4 2026-06-22). Round-robin zip — ровный alternation,
  // оставшиеся одинокого бренда уходят в хвост.
  const cars = useMemo(() => {
    if (!rawCars.length) return rawCars;
    const omoda = rawCars.filter((c) => /omoda/i.test(c.make));
    const jaecoo = rawCars.filter((c) => /jaecoo/i.test(c.make));
    const other = rawCars.filter((c) => !/omoda|jaecoo/i.test(c.make));
    const interleaved = [];
    const maxLen = Math.max(omoda.length, jaecoo.length);
    for (let i = 0; i < maxLen; i++) {
      if (omoda[i]) interleaved.push(omoda[i]);
      if (jaecoo[i]) interleaved.push(jaecoo[i]);
    }
    return [...interleaved, ...other];
  }, [rawCars]);

  // Hero photo: первая доступная фотка первой машины из инвентори. Если нет —
  // фон уходит в чистый --salon-bg dark fallback (без overlay).
  const heroPhoto = useMemo(() => {
    if (!cars.length) return null;
    const firstWithImg = cars.find((c) => getDisplayImages(c).length > 0);
    if (!firstWithImg) return null;
    return getDisplayImages(firstWithImg)[0] || null;
  }, [cars]);

  // Swipe-affordance edge chevrons (#1022, same UX как на CarLandingPage).
  // Скрываем после первого committed swipe + persist для повторных визитов.
  const [swipeHintsDismissed, setSwipeHintsDismissed] = useState(() => {
    try {
      return typeof window !== 'undefined'
        && window.localStorage?.getItem(SWIPE_HINT_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const dismissSwipeHints = useCallback(() => {
    setSwipeHintsDismissed((prev) => {
      if (prev) return prev;
      try { window.localStorage?.setItem(SWIPE_HINT_STORAGE_KEY, '1'); } catch {
        /* private mode / iframe restrictions — игнорируем */
      }
      return true;
    });
  }, []);

  const handleCardClick = (car) => {
    dealerNavigate(`/cars/${car.id}/landing`);
  };

  const handleViewCatalog = () => {
    dealerNavigate('/showroom');
  };

  const shellStyle = heroPhoto
    ? { '--salon-hero-bg': `url("${heroPhoto}")` }
    : undefined;

  return (
    <div className="salon-landing" style={shellStyle}>
      {/* SalonHeader сам делает лого инертным на `/salon` (мы уже на Welcome).
          LanguageSelector — через showLanguage (Маша 23.06 «добавь на велком тоже»). */}
      {/* Brands строка убрана (Маша 23.06): OMODA/JAECOO теперь живут
          как badge'ы в углу фотографий карточек, не в общем header.
          Header остаётся с Autodis-лого + LanguageSelector. */}
      <SalonHeader brands={[]} showLanguage />

      <main className="salon-landing__main" aria-busy={loading || undefined}>
        {loading && (
          <div className="salon-landing__state">{t('salon.state.loading')}</div>
        )}
        {!loading && error && (
          <div
            className="salon-landing__state salon-landing__state--error"
            role="alert"
          >
            {t('salon.state.error')}
          </div>
        )}
        {!loading && !error && cars.length === 0 && (
          <div className="salon-landing__state">{t('salon.state.empty')}</div>
        )}
        {!loading && !error && cars.length > 0 && (
          <div className="salon-landing__carousel-wrap">
            <AutoScrollCarousel
              ariaLabel={t('salon.aria.carousel')}
              onUserInteract={dismissSwipeHints}
            >
              {cars.map((car) => (
                <SalonCarCard
                  key={car.id}
                  car={car}
                  onClick={handleCardClick}
                />
              ))}
            </AutoScrollCarousel>
            {/* Bouncing chevrons signal "swipe between cars" — same UX как
                на CarLandingPage (#1022 review). Скрываются после первого
                user-interaction в карусели (touch/wheel/pointer) +
                persist в localStorage. */}
            {!swipeHintsDismissed && (
              <>
                <span
                  className="salon-landing__swipe-hint salon-landing__swipe-hint--left"
                  aria-hidden="true"
                >
                  <ChevronLeft size={28} weight="regular" />
                </span>
                <span
                  className="salon-landing__swipe-hint salon-landing__swipe-hint--right"
                  aria-hidden="true"
                >
                  <ChevronRight size={28} weight="regular" />
                </span>
              </>
            )}
          </div>
        )}
        {/* CTA каталога под карточками — текстовая кнопка (Маша 24.06:
            «текстовая кнопка перехода в каталог под карточками»). Glass-pill,
            матчащийся с выбором языка, — это кнопка каталога В ХЕДЕРЕ
            (`.landing-close-fab` на CarLandingPage), не эта. */}
        <button
          type="button"
          className="salon-landing__cta"
          onClick={handleViewCatalog}
        >
          {t('salon.cta.viewAllCars')}
          <ArrowRight
            className="salon-landing__cta-arrow"
            size={22}
            weight="regular"
            aria-hidden="true"
          />
        </button>
      </main>
    </div>
  );
};

export default SalonLandingPage;
